import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ConsentDialogComponent } from '@app/consent-dialog/consent-dialog.component';
import { environment } from '@environments/environment';
import { AccountService, userInterface } from './account.service';

// localStorage key used as a fast-path cache for cookie/T&C consent;
// the backend (User.consent_accepted_at/consent_version) stays the source of truth.
const CONSENT_STATE_KEY = 'consentState';

@Injectable({
  providedIn: 'root',
})
export class ConsentService {
  constructor(
    private accountService: AccountService,
    private dialog: MatDialog,
  ) {}

  ensureConsent(): Observable<boolean> {
    const cached = this.readConsentState();
    if (cached?.version === environment.consentVersion) {
      return of(true);
    }

    return this.accountService.getUserDetail<userInterface>().pipe(
      switchMap((user) => {
        if (
          user.consent_accepted_at &&
          user.consent_version === environment.consentVersion
        ) {
          this.writeConsentState(
            environment.consentVersion,
            user.consent_accepted_at,
          );
          return of(true);
        }
        return this.openConsentDialog();
      }),
      catchError((err: any) => {
        // Fail-closed: se non riusciamo a verificare lo stato del consenso non
        // concediamo l'accesso (prima ritornava true, lasciando passare tutti).
        console.error('Error checking consent state:', err);
        return of(false);
      }),
    );
  }

  private openConsentDialog(): Observable<boolean> {
    return this.dialog
      .open(ConsentDialogComponent, {
        width: '90vw',
        maxWidth: '600px',
        height: '320px',
        maxHeight: '90vh',
        disableClose: true,
      })
      .afterClosed()
      .pipe(
        switchMap((accepted) => {
          if (!accepted) {
            // Rifiuto (o chiusura): NEGA l'accesso e logga il rifiuto in
            // ConsentRecord. Il dialog ha gia' avviato il redirect a /auth/logout.
            return this.accountService
              .rejectConsent(environment.consentVersion)
              .pipe(
                catchError((err: any) => {
                  console.error('Error saving consent rejection:', err);
                  return of(null);
                }),
                map(() => false),
              );
          }
          return this.accountService
            .acceptConsent<userInterface>(environment.consentVersion)
            .pipe(
              switchMap((user) => {
                this.writeConsentState(
                  environment.consentVersion,
                  user.consent_accepted_at ?? null,
                );
                return of(true);
              }),
              catchError((err: any) => {
                // Save fallito: il consenso non e' stato registrato lato server,
                // quindi NON concediamo l'accesso (prima ritornava true).
                console.error('Error saving consent:', err);
                return of(false);
              }),
            );
        }),
      );
  }

  private readConsentState(): {
    version: string;
    acceptedAt: string | null;
  } | null {
    try {
      const raw = localStorage.getItem(CONSENT_STATE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  private writeConsentState(version: string, acceptedAt: string | null) {
    try {
      localStorage.setItem(
        CONSENT_STATE_KEY,
        JSON.stringify({ version, acceptedAt }),
      );
    } catch {
      // ignore storage errors (e.g. private browsing)
    }
  }
}
